import { useTranslate } from "ra-core";
import { Link } from "react-router";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { useExperimentIndex } from "./api";
import { ExperimentImage } from "./ExperimentImage";
import type { ExperimentSummary } from "./types";

export const LabResultsPage = () => {
  const translate = useTranslate();
  const index = useExperimentIndex();

  return (
    <div className="max-w-6xl mx-auto my-8 px-4">
      <div className="flex items-baseline justify-between mb-6">
        <h1 className="text-2xl font-semibold">
          {translate("crm.lab_results.title")}
        </h1>
        <p className="text-sm text-muted-foreground">
          {translate("crm.lab_results.subtitle")}
        </p>
      </div>
      {index.isLoading && (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} className="h-64" />
          ))}
        </div>
      )}
      {index.isError && (
        <Card>
          <CardContent className="py-6 text-sm text-destructive">
            {translate("crm.lab_results.load_error")}:{" "}
            {(index.error as Error).message}
          </CardContent>
        </Card>
      )}
      {index.data && index.data.experiments.length === 0 && (
        <Card>
          <CardContent className="py-10 text-center text-muted-foreground">
            {translate("crm.lab_results.empty")}
          </CardContent>
        </Card>
      )}
      {index.data && index.data.experiments.length > 0 && (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
          {index.data.experiments.map((experiment) => (
            <ExperimentCard key={experiment.slug} experiment={experiment} />
          ))}
        </div>
      )}
    </div>
  );
};

LabResultsPage.path = "/lab-results";

const ExperimentCard = ({ experiment }: { experiment: ExperimentSummary }) => {
  const translate = useTranslate();
  return (
    <Link
      to={`${LabResultsPage.path}/${experiment.slug}`}
      className="block group"
    >
      <Card className="h-full overflow-hidden transition-shadow group-hover:shadow-md">
        <div className="aspect-[4/3] bg-muted overflow-hidden">
          {experiment.cover ? (
            <ExperimentImage
              media={experiment.cover}
              alt={experiment.title}
              className="w-full h-full object-cover"
            />
          ) : null}
        </div>
        <CardHeader className="pb-2">
          <CardTitle className="text-base leading-snug">
            {experiment.title}
          </CardTitle>
          <p className="text-xs text-muted-foreground">
            {formatDate(experiment.date)}
            {experiment.author ? ` · ${experiment.author}` : ""}
          </p>
        </CardHeader>
        <CardContent className="pt-0 space-y-2">
          {experiment.summary && (
            <p className="text-sm text-muted-foreground line-clamp-3">
              {experiment.summary}
            </p>
          )}
          <div className="flex flex-wrap gap-1">
            {typeof experiment.photo_count === "number" && (
              <Badge variant="secondary">
                {translate(
                  experiment.media_type === "video"
                    ? "crm.lab_results.videos"
                    : "crm.lab_results.photos",
                  { smart_count: experiment.photo_count },
                )}
              </Badge>
            )}
            {typeof experiment.arm_count === "number" && (
              <Badge variant="secondary">
                {translate("crm.lab_results.arms", {
                  smart_count: experiment.arm_count,
                })}
              </Badge>
            )}
            {experiment.tags?.map((tag) => (
              <Badge key={tag} variant="outline">
                {tag}
              </Badge>
            ))}
          </div>
        </CardContent>
      </Card>
    </Link>
  );
};

export function formatDate(value: string) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}
